// Date helpers for the Knesset schedule. Session dates are stored as Israel-local
// strings without an offset ('2026-10-08T09:00:00'), so everything here works on
// strings in Israel time and never on the browser's own time zone.

const pad2 = (n: number): string => (n < 10 ? '0' : '') + n;

// Today's date in Israel, 'YYYY-MM-DD'
export const israelToday = (): string =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Jerusalem' });

// Current Israel wall-clock time, 'YYYY-MM-DDTHH:mm:ss' (same format as stored session dates)
export const israelNow = (): string => israelDateTimeAt(Date.now());

// Israel wall-clock time at an instant (ms since the epoch), 'YYYY-MM-DDTHH:mm:ss'
export const israelDateTimeAt = (ms: number): string =>
  new Date(ms).toLocaleString('sv-SE', { timeZone: 'Asia/Jerusalem' }).replace(' ', 'T');

// 'YYYY-MM-DD' + n days, using UTC arithmetic so DST and the local zone never shift the day
export const addDaysYmd = (ymd: string, n: number): string => {
  const d = new Date(Date.UTC(
    parseInt(ymd.slice(0, 4), 10), parseInt(ymd.slice(5, 7), 10) - 1, parseInt(ymd.slice(8, 10), 10) + n
  ));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

// 'YYYY-MM-DD' → 'YYYY-MM'
export const ymOf = (ymd: string): string => ymd.slice(0, 7);

// 'YYYY-MM' + n months
export const shiftYm = (ym: string, n: number): string => {
  const d = new Date(Date.UTC(parseInt(ym.slice(0, 4), 10), parseInt(ym.slice(5, 7), 10) - 1 + n, 1));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
};

// Normalises a stored date-time to 'YYYY-MM-DDTHH:mm:ss' (drops fractions, adds missing seconds)
const normDateTime = (dt: string): string => {
  const s = dt.slice(0, 19);
  return s.length === 16 ? `${s}:00` : s;
};

// Israel-local date-time string + h hours, as UTC-naive arithmetic on the fields
const addHoursDateTime = (dt: string, h: number): string => {
  const d = new Date(Date.UTC(
    parseInt(dt.slice(0, 4), 10), parseInt(dt.slice(5, 7), 10) - 1, parseInt(dt.slice(8, 10), 10),
    parseInt(dt.slice(11, 13), 10) + h, parseInt(dt.slice(14, 16), 10), parseInt(dt.slice(17, 19), 10) || 0
  ));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}` +
    `T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}`;
};

// Live = not cancelled and start <= now <= end, where end is finishDate or start + 3h.
// Compared as Israel-local strings, so it's correct in any browser time zone. This is the
// time rule; utils/live mergeLive prefers the Knesset's live feed and falls back to it.
export const isLiveNow = (s: { date: string; finishDate?: string | null; isCancelled?: boolean }): boolean =>
  isLiveAt(s, israelNow());

// The same rule at a given Israel wall-clock time ('YYYY-MM-DDTHH:mm:ss', see israelDateTimeAt)
export const isLiveAt = (s: { date: string; finishDate?: string | null; isCancelled?: boolean }, now: string): boolean => {
  if (s.isCancelled || !s.date) return false;
  const start = normDateTime(s.date);
  const end = s.finishDate ? normDateTime(s.finishDate) : addHoursDateTime(start, 3);
  return start <= now && now <= end;
};
